import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/AddUnit.css"; // Reusing the existing stylesheet

const AddDepartmentCustomer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [regNo, setRegNo] = useState(""); // Changed from 'discount' to 'regNo'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL || "";

  // Fetch departments for the dropdown menu
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(`${API_URL}department-list.php`);
        const data = await response.json();
        if (Array.isArray(data.data)) {
          setDepartments(data.data);
        } else {
          showToast("Could not fetch departments", "error");
        }
      } catch (error) {
        showToast("Network error while fetching departments", "error");
      }
    };
    fetchDepartments();
  }, [API_URL]);

  useEffect(() => {
    // Check if we're in edit mode
    if (location.state && location.state.registration && location.state.isEditing) {
      const { registration } = location.state;
      setDepartmentId(registration.dept_name_id || registration.dept_id || "");
      setRegNo(registration.reg_no || "");
      setRegistrationId(registration.id);
      setIsEditing(true);
    } else {
      // Reset to add mode if no edit state
      setDepartmentId("");
      setRegNo("");
      setRegistrationId(null);
      setIsEditing(false);
    }
  }, [location.state]);

  // Cleanup effect to reset state when component unmounts or location changes
  useEffect(() => {
    return () => {
      setDepartmentId("");
      setRegNo("");
      setRegistrationId(null);
      setIsEditing(false);
    };
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!departmentId) {
      showToast("Please select a department", "error");
      return;
    }
    if (!regNo.trim()) {
      // Updated validation
      showToast("Please enter a registration number", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = isEditing ? "edit-department-reg.php" : "add-department-reg.php";
      const body = isEditing 
        ? { 
            id: registrationId, 
            dept_name_id: Number(departmentId), 
            reg_no: regNo 
          }
        : {
            dept_name_id: Number(departmentId),
            reg_no: regNo,
          };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.message && data.message.includes("successfully")) {
        const action = isEditing ? "updated" : "added";
        showToast(`Department registration ${action} successfully!`, "success");
        setTimeout(() => {
          navigate("/department-customer-list");
        }, 1500);
      } else if (data.status === "success") {
        // Fallback for APIs that use status field
        const action = isEditing ? "updated" : "added";
        showToast(`Department registration ${action} successfully!`, "success");
        setTimeout(() => {
          navigate("/department-customer-list");
        }, 1500);
      } else {
        showToast(data.message || `Failed to ${isEditing ? 'update' : 'add'} registration`, "error");
      }
    } catch (error) {
      showToast(`Network error: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-category-content">
      <div className="page-header" style={{ display: "block" }}>
        <h1>{isEditing ? "Edit Department Registration" : "Register Customer to Department"}</h1>
        <p>{isEditing ? "Update department registration information" : "Create a new customer registration for a department"}</p>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="departmentId">Department Name</label>
            <select
              id="departmentId"
              name="departmentId"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              disabled={isSubmitting || departments.length === 0}
              required
            >
              <option value="">Select a Department</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.dept_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="regNo">Registration Number</label>
            <input
              type="text" // Changed from 'number'
              id="regNo"
              name="regNo"
              value={regNo}
              onChange={(e) => setRegNo(e.target.value)}
              placeholder="e.g., REG-12345" // Updated placeholder
              required
              disabled={isSubmitting}
            />
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting 
              ? (isEditing ? "Updating Registration..." : "Registering Customer...") 
              : (isEditing ? "Update Registration" : "Register Customer")
            }
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDepartmentCustomer;
